import { Accordion, Box, Checkbox, Typography } from '@mui/material'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import React, { PropsWithChildren, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { BiChevronDown } from 'react-icons/bi'
import Fade from 'react-reveal/Fade'
import { useLocation } from 'react-router-dom'

import usePersistedState from 'src/commons/hooks/usePersistedState'

export const useSubtitle = function (
  subtitle: string | undefined,
  allSelected: boolean,
  nothingSelected: boolean,
  values: unknown[] | undefined,
  items: unknown[] | undefined
) {
  const { t } = useTranslation()

  return useMemo(() => {
    if (subtitle !== undefined) {
      return subtitle
    }

    if (allSelected) {
      return t('filters.checkbox.allSelected')
    } else if (nothingSelected) {
      return t('filters.checkbox.nothingSelected')
    }

    return t('filters.checkbox.someSelected', { size: values?.length ?? 0, length: items?.length ?? 0 })
  }, [subtitle, allSelected, nothingSelected, values?.length, items?.length])
}

export interface ICollapsibleFilterProps<ValueType = unknown> {
  defaultExpanded?: boolean
  displayCheckbox?: boolean
  errorMessage?: string
  filterId: string
  items?: ValueType[]
  selectAll?: () => void
  subtitle?: string
  title: string
  unselectAll?: () => void
  values?: ValueType[]
}

export default function CollapsibleFilter(props: PropsWithChildren<ICollapsibleFilterProps>) {
  const { pathname } = useLocation()
  const [expanded, setExpanded] = usePersistedState(
    `plato.${pathname}.filters.${props.filterId}.expanded`,
    Boolean(props.defaultExpanded)
  )

  const isAllSelected = !!props.items?.length && props.values?.length === props.items.length
  const isNothingSelected = !props.values?.length
  const subtitle = useSubtitle(props.subtitle, isAllSelected, isNothingSelected, props.values, props.items)

  return (
    <Accordion
      disableGutters
      expanded={expanded}
      onChange={(_, expanded) => setExpanded(expanded)}
      square
      elevation={0}
      sx={{
        margin: 0,
        paddingY: '1em',
        width: '100%',
        '&.Mui-expanded': {
          '&:before': {
            opacity: 1,
          },
        },
      }}
    >
      <AccordionSummary
        expandIcon={<BiChevronDown />}
        sx={{
          mt: 0,
          mb: 1,
          px: '12px',
          fontSize: '24px',
          display: 'flex',
          alignItems: 'flex-start',
          '&.Mui-expanded': {
            minHeight: 0,
            margin: 0,
            '&:before': {
              opacity: 1,
            },
          },
          '& .MuiAccordionSummary-content.Mui-expanded': {
            margin: 0,
          },
          '& .MuiAccordionSummary-content': {
            margin: 0,
          },
        }}
      >
        {props.displayCheckbox && (
          <Checkbox
            sx={{
              p: 0,
              minWidth: '18px',
              mr: '7px',
              mt: '3px',
              alignSelf: 'flex-start',
              '& .MuiSvgIcon-root': { fontSize: 26 },
            }}
            color="primary"
            checked={isAllSelected}
            indeterminate={!!props.values?.length && !!props.items?.length && props.values.length < props.items.length}
            onChange={(_e, checked) => (checked ? props.selectAll?.() : props.unselectAll?.())}
            onClick={(e) => e.stopPropagation()}
          />
        )}

        <Box display="flex" flexDirection="column" width="100%">
          <Typography sx={{ mt: 1, mb: '3px' }} variant="h5" title={props.title}>
            {props.title}
          </Typography>
          {props.errorMessage ? (
            <Box display="flex" minHeight="1rem">
              <Typography variant="h6" color={'error'}>
                {props.errorMessage}
              </Typography>
            </Box>
          ) : (
            <Box display="flex" minHeight="1rem">
              <Fade when={!expanded} delay={100}>
                <Typography variant="h6">{subtitle}</Typography>
              </Fade>
            </Box>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          px: '10px',
        }}
      >
        {props.children}
      </AccordionDetails>
    </Accordion>
  )
}
